import React, { useEffect, useState } from "react";
import { Printer, X } from "lucide-react";
import { toast } from "sonner";
import { drw, errText, sheetThumbUrl } from "./api";
import SymbolStyle from "./SymbolStyle";

export const SHAPES = ["rectangle", "ellipse", "triangle", "polygon", "diamond", "line", "cloud"];

/** DRW-8 — a swatch drawn from the registry: shape + colour, dashed when unmapped. */
export function Swatch({ shape, color, fill, unmapped, size = 14 }) {
  const c = unmapped ? "#737373" : color;
  const common = { stroke: c, strokeWidth: 1.6, fill: fill && !unmapped ? c : "none", fillOpacity: 0.25, strokeDasharray: unmapped ? "2 2" : undefined };
  const h = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" data-testid="legend-swatch" data-shape={shape}>
      {shape === "ellipse" ? <ellipse cx={h} cy={h} rx={h - 1.5} ry={h - 1.5} {...common} />
        : shape === "triangle" ? <polygon points={`${h},1.5 ${size - 1.5},${size - 1.5} 1.5,${size - 1.5}`} {...common} />
        : shape === "diamond" ? <polygon points={`${h},1.5 ${size - 1.5},${h} ${h},${size - 1.5} 1.5,${h}`} {...common} />
        : shape === "line" ? <line x1={1.5} y1={size - 1.5} x2={size - 1.5} y2={1.5} {...common} />
        : shape === "cloud" ? <circle cx={h} cy={h} r={h - 2} {...common} strokeDasharray="3 1.5" />
        : shape === "polygon" ? <polygon points={`${h},1.5 ${size - 1.5},${h * 0.72} ${size - 2.6},${size - 1.5} ${2.6},${size - 1.5} 1.5,${h * 0.72}`} {...common} />
        : <rect x={1.5} y={1.5} width={size - 3} height={size - 3} {...common} />}
    </svg>
  );
}

/** The legend of takeoff marks on this page. Each row is a checkbox: checked = that symbol's
 *  marks are on the plan, unchecked = hidden (DRW-A §17). The swatch opens the style popover;
 *  a registry edit re-styles every mark of the symbol on the plan without a reload. */
export default function Legend({ sheetId, sheetNumber, projectId, refreshKey, canEdit, hiddenKeys, onToggleKey, onSetHidden, onRestyle, onClose, compact }) {
  const [data, setData] = useState(null);
  const [reg, setReg] = useState({});
  const hidden = hiddenKeys || new Set();
  useEffect(() => {
    drw.get(`/sheets/${sheetId}/legend`).then(({ data: d }) => setData(d)).catch((e) => toast.error(errText(e, "Could not load the legend")));
    drw.get(`/projects/${projectId}/symbols`).then(({ data: s }) => setReg(Object.fromEntries(s.symbols.map((x) => [x.key, x])))).catch(() => {});
  }, [sheetId, projectId, refreshKey]);
  const patch = async (row, body) => {
    try {
      let r = reg[row.key];
      if (!r) {
        const { data } = await drw.post(`/projects/${projectId}/symbols/ensure`, { subject: row.subject, label: row.label_full || row.label, scope_code: row.scope_code || "", shape: row.shape, color: row.color, fill: !!row.fill });
        r = data; setReg((m) => ({ ...m, [row.key]: data }));
      }
      const { data: upd } = await drw.patch(`/symbols/${r.id}`, body);
      setReg((m) => ({ ...m, [row.key]: upd }));
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.key === row.key ? { ...x, ...body } : x)) }));
      onRestyle?.();
    } catch (e) { toast.error(errText(e)); }
  };
  const print = () => {
    const w = window.open("", "_blank");
    if (!w || !data) return;
    const rows = data.rows.map((r) => `<tr><td><span style="display:inline-block;width:12px;height:12px;border:2px ${r.unmapped ? "dashed #737373" : "solid " + r.color};border-radius:${r.shape === "ellipse" ? "50%" : 0}"></span></td><td>${r.scope_code || "unmapped"}</td><td>${r.label}</td><td>${r.description || ""}</td><td>${r.subject}</td><td>${r.qty}</td></tr>`).join("");
    w.document.write(`<html><head><title>${sheetNumber} — legend</title><style>body{font-family:sans-serif;margin:0;display:flex}img{max-height:100vh;max-width:70vw}aside{padding:12px;font-size:11px}table{border-collapse:collapse}td,th{padding:2px 6px;border-bottom:1px solid #ddd;text-align:left}</style></head><body><img src="${sheetThumbUrl(sheetId, 1800)}"/><aside data-testid="print-legend"><h3>${sheetNumber} — takeoff legend</h3><table><tr><th></th><th>Scope</th><th>Item</th><th>Description</th><th>Subject</th><th>Qty</th></tr>${rows}</table><p>Set totals: ${data.set_totals.map((t) => `${t.scope_code} ${t.units}`).join(" · ")}</p></aside></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 600);
  };
  const allKeys = (data?.rows || []).map((r) => r.key);
  const shownCount = allKeys.filter((k) => !hidden.has(k)).length;
  const body = (
    <div className="flex flex-col h-full" data-testid="legend-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b text-sm font-semibold">
        Legend <span className="text-xs font-normal text-neutral-500">{data ? `${shownCount}/${data.rows.length} shown` : ""}</span>
        <button type="button" onClick={print} className="ml-auto h-7 px-2 rounded-md border text-xs flex items-center gap-1" data-testid="legend-print"><Printer className="w-3 h-3" /> Print page</button>
        <button type="button" onClick={onClose} className="h-7 w-7 grid place-items-center rounded-md border" aria-label="Hide legend" data-testid="legend-hide"><X className="w-3.5 h-3.5" /></button>
      </div>
      {data && data.rows.length > 0 && (
        <div className="flex items-center gap-3 px-3 py-1.5 border-b text-[11px] text-neutral-600">
          <span>Check what you want to see.</span>
          <button type="button" onClick={() => onSetHidden?.(new Set())} className="ml-auto underline" data-testid="legend-show-all">All</button>
          <button type="button" onClick={() => onSetHidden?.(new Set(allKeys))} className="underline" data-testid="legend-show-none">None</button>
        </div>
      )}
      <div className="overflow-auto flex-1 text-xs">
        {!data && <div className="p-3 text-neutral-500">Loading…</div>}
        {data?.rows.length === 0 && <div className="p-3 text-neutral-500">No takeoff marks on this page.</div>}
        {data?.rows.map((r) => {
          const on = !hidden.has(r.key);
          return (
            <div key={r.key} className={`px-3 py-1.5 border-b ${on ? "" : "opacity-50"}`} data-testid={`legend-row-${r.key}`} data-qty={r.qty} data-visible={on ? "1" : "0"}>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={on} onChange={() => onToggleKey?.(r.key)} className="h-4 w-4 shrink-0 accent-neutral-900" aria-label={`Show ${r.label}`} data-testid={`legend-visible-${r.key}`} />
                <SymbolStyle shape={r.shape} color={r.color} fill={r.fill} unmapped={r.unmapped} canEdit={canEdit}
                             onPatch={(body) => patch(r, body)} testId={`legend-shape-${r.key}`} />
                <span className="font-semibold w-12 truncate">{r.unmapped ? <span className="text-neutral-500 italic">unmapped</span> : r.scope_code}</span>
                <span className="truncate flex-1" title={r.label_full}>{r.label}</span>
                {r.app_added > 0 && <span className="w-1.5 h-1.5 rounded-full bg-sky-600" title={`${r.app_added} added in the app`} data-testid="legend-origin-dot" />}
                <span className="font-mono font-bold tabular-nums" data-testid={`legend-qty-${r.key}`}>{r.qty}</span>
              </div>
              {r.description && <div className="text-[11px] text-neutral-700 pl-[3.25rem] leading-snug" title={r.description} data-testid={`legend-desc-${r.key}`}>{r.description}</div>}
              <div className="text-[10px] text-neutral-500 pl-[3.25rem] truncate">{r.rooms.length ? `Rooms: ${r.rooms.map((x) => `${x.room_number} ×${x.units}`).join(", ")}` : "no room assigned"}</div>
            </div>
          );
        })}
      </div>
      {data && data.set_totals.length > 0 && (
        <div className="px-3 py-2 border-t text-[11px] text-neutral-700 flex flex-wrap gap-x-3" data-testid="legend-totals">
          <span className="font-semibold">Set:</span>
          {data.set_totals.map((t) => <span key={t.scope_code}>{t.scope_code} <b>{t.units}</b></span>)}
        </div>
      )}
    </div>
  );
  if (compact) {
    return <div className="fixed left-0 right-0 bottom-0 z-[60] bg-white border-t rounded-t-2xl shadow-2xl max-h-[55vh] flex flex-col" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} data-testid="legend-sheet">{body}</div>;
  }
  // DRW-A §18 — an overlay, never a flex sibling: opening it must not change the stage width.
  return <div className="absolute right-0 top-0 bottom-0 z-30 w-72 bg-white border-l shadow-xl flex flex-col" data-testid="legend-side">{body}</div>;
}
